import React, { useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { Redirect, Switch, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CrumbRoute from 'components/CrumbRoute';
import Layout from 'components/Layout/Layout';
import AccountStepper from 'components/AccountStepper/AccountStepper';

import { EditAccountStore } from 'stores/editAccountStore';
import { ACCOUNTS_EDIT_BASIC, Params, draftSteps } from './accountEditRoutes';
import { ACCOUNTS_EDIT, ACCOUNTS_DRAFTS } from 'routes';
import { AccountStatus } from 'enums';

import { RootStore } from 'stores/rootStore';
import { API_LOG_LOAD_ACCOUNT } from 'api/endpoints';

interface Props {
  editAccountStore: EditAccountStore;
  rootStore: RootStore;
}

const EditAccount: React.FC<Props> = ({ editAccountStore, rootStore }) => {
  const { step, accountStatus } = editAccountStore;
  const { accountId } = useParams<Params>();
  const { t } = useTranslation();
  const mockAccount = accountStatus === AccountStatus.MOCK;

  useEffect(() => {
    (async () => {
      await editAccountStore.fetchBasicData(accountId);
      rootStore.stores.apiStore.post(API_LOG_LOAD_ACCOUNT(accountId));
      rootStore.stores.uiStore.setCurrentAccountId(accountId);

      return () => {
        rootStore.stores.uiStore.unsetCurrentAccountId();
      };
    })();
  }, [accountId, rootStore.stores.apiStore, rootStore.stores.uiStore, editAccountStore]);

  return (
    <Layout title={t('pageTitle_accountEdit', { accountName: editAccountStore.accountName })} mockAccount={mockAccount}>
      <AccountStepper active={step} />

      <Switch>
        <Redirect exact from={ACCOUNTS_EDIT()} to={ACCOUNTS_EDIT_BASIC(accountId)} />
        {draftSteps.map(({ route, title: routeTitle, component }) => (
          <CrumbRoute
            key={route && route()}
            path={route && route()}
            component={component}
            title={[t('navMenu_accounts'), editAccountStore.accountName || t('general_newAccount'), t(routeTitle)]}
            home={ACCOUNTS_DRAFTS}
          />
        ))}
      </Switch>
    </Layout>
  );
};

export default inject('editAccountStore', 'rootStore')(observer(EditAccount));
